/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react'
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import * as RadixDialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { ReactNode } from 'react'
import { colorWithOpacity, useTheme } from 'ui'

const TRANSITION_DURATION = 250

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}

export function Drawer({ open, onOpenChange, children }: Props) {
  const theme = useTheme()

  const overlayShow = keyframes({
    from: { opacity: 0 },
    to: { opacity: 1 },
  })
  const overlayHide = keyframes({
    from: { opacity: 1 },
    to: { opacity: 0 },
  })

  const contentShow = keyframes({
    from: { transform: 'translateX(-100%)' },
    to: { transform: 'translateX(0)' },
  })
  const contentHide = keyframes({
    from: { transform: 'translateX(0)' },
    to: { transform: 'translateX(-100%)' },
  })

  const styles = {
    overlay: css({
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.32)',
      '&[data-state=open]': {
        animation: `${overlayShow} ${TRANSITION_DURATION}ms ${theme.web.easing.curveLinearToEaseOut}`,
      },
      '&[data-state=closed]': {
        animation: `${overlayHide} ${TRANSITION_DURATION}ms ${theme.web.easing.curveLinearToEaseOut}`,
      },
    }),
    content: css({
      position: 'fixed',
      top: 0,
      left: 0,
      bottom: 0,
      width: 320,
      overflowY: 'auto',
      background: theme.colors.surface,
      boxShadow: theme.web.shadow.overlay,
      '&[data-state=open]': {
        animation: `${contentShow} ${TRANSITION_DURATION}ms ${theme.web.easing.curveLinearToEaseOut}`,
      },
      '&[data-state=closed]': {
        animation: `${contentHide} ${TRANSITION_DURATION}ms ${theme.web.easing.curveLinearToEaseOut}`,
      },
    }),
    close: css({
      position: 'absolute',
      top: 10,
      right: 10,
      cursor: 'pointer',
      '&:hover': {
        opacity: 0.6,
      },
    }),
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay css={styles.overlay} />
        <RadixDialog.Content aria-describedby={undefined} css={styles.content}>
          <VisuallyHidden><RadixDialog.Title>Drawer</RadixDialog.Title></VisuallyHidden>
          <RadixDialog.Close asChild={true}>
            <div css={styles.close}>
              <FontAwesomeIcon
                icon={faXmark}
                size={21}
                color={colorWithOpacity(theme.colors.foreground, 0.7)}
              />
            </div>
          </RadixDialog.Close>
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}
